import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MicroserviceHttpService } from '../../shared/microservice-http.service';
import { IGrantUserEntitlementRequest, IUserAccess } from '../interfaces/iuser-access.interface';

/**
 * One person's effective access and the direct-grant exception path: grant one entitlement to one
 * person without a group, and revoke such a grant. A grant held through a group is never touched
 * here; it goes when the person leaves the group. Reads need the Admin role; both writes need
 * Admin:Entitlements:Manage.
 */
@Injectable({ providedIn: 'root' })
export class UserAccessService extends MicroserviceHttpService {
	constructor() {
		super('user', 'users');
	}

	/** Every entitlement the person holds, labelled direct and/or by the groups that grant it, plus memberships and recent changes. */
	getUserAccess(userID: number): Observable<IUserAccess> {
		return this.get<IUserAccess>(`${this.serviceUrl}/${userID}/access`);
	}

	/**
	 * Grants an entitlement directly, or refreshes the end date of a direct grant the person already
	 * has. 400 on a system (`entitlement_system_managed`) or retired (`entitlement_retired`) entitlement.
	 * Returns the person's updated access.
	 */
	grantEntitlement(userID: number, request: IGrantUserEntitlementRequest): Observable<IUserAccess> {
		return this.post<IUserAccess>(`${this.serviceUrl}/${userID}/entitlements`, request);
	}

	/**
	 * Revokes the DIRECT grant only. 404 (`user_entitlement_not_found`) when the person has no direct
	 * grant of it — including when they hold it through a group. Returns the updated access.
	 */
	revokeEntitlement(userID: number, entitlementID: number): Observable<IUserAccess> {
		return this.delete<IUserAccess>(`${this.serviceUrl}/${userID}/entitlements/${entitlementID}`);
	}
}
